import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, RotateCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { listVideoUploadJobs } from '@/api/adminVideos';
import { getArchiveVideoShareUrl } from '@/api/content';

const STATUS_LABELS = {
  reserved: { label: '準備中', className: 'border-[#d4c4b0] text-[#5d4529]' },
  transcoding: { label: '変換中', className: 'border-[#d4c4b0] text-[#5d4529]' },
  finalizing: { label: '登録中', className: 'border-[#d4c4b0] text-[#5d4529]' },
  completed: { label: '完了', className: 'border-[#a8c39a] text-[#4d6b40]' },
  failed: { label: '失敗', className: 'border-[#e5c4bb] text-[#b4462f]' },
  canceled: { label: '中止', className: 'border-[#d4c4b0] text-[#6b5540]' },
};

const IN_PROGRESS_STATUSES = ['reserved', 'transcoding', 'finalizing'];

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 className="h-4 w-4 text-[#6b8f5d]" />;
  if (status === 'failed' || status === 'canceled') {
    return <AlertCircle className="h-4 w-4 text-[#b4462f]" />;
  }
  return <Loader2 className="h-4 w-4 animate-spin text-[#8b6f47]" />;
}

/**
 * 直近のアップロードジョブ一覧。
 * ブラウザを閉じたあとでも、変換中か・完了したか・失敗したかをここで確認できる。
 */
export default function AdminVideoUploadJobList({ enabled = true, limit = 3, className = '' }) {
  const jobsQuery = useQuery({
    queryKey: ['admin-video-upload-jobs', limit],
    queryFn: () => listVideoUploadJobs({ limit }),
    enabled,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
    // 未デプロイのAPIを叩いた場合の404を何度も追いかけない
    retry: false,
  });

  const jobs = jobsQuery.data ?? [];
  const inProgressCount = jobs.filter((job) => IN_PROGRESS_STATUSES.includes(job.status)).length;

  // 該当が無いときは枠ごと描画しない（呼び出し側の余白も出さないため className はここで当てる）
  if (jobsQuery.isLoading) return null;
  // 補助的な一覧なので、取得に失敗しても画面にエラーを出さず黙って隠す。
  // フロントを先にデプロイして Lambda が古い（このAPIが404）状態でも、他の機能に影響させないため。
  if (jobsQuery.isError) return null;
  if (jobs.length === 0) return null;

  return (
    <div className={`rounded-2xl border border-[#e8dccd] bg-white p-5 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[#2d1810]">
          最近のアップロード
          {inProgressCount > 0 && (
            <Badge className="ml-2 bg-[#8b6f47] text-[10px] text-[#f5ede3]">
              処理中 {inProgressCount}件
            </Badge>
          )}
        </h2>
        <Button
          type="button"
          variant="outline"
          onClick={() => jobsQuery.refetch()}
          className="h-8 gap-1 border-[#d4c4b0] text-xs text-[#5d4529] hover:bg-[#f5ede3]"
        >
          <RotateCw className={`h-3 w-3 ${jobsQuery.isFetching ? 'animate-spin' : ''}`} />
          更新
        </Button>
      </div>

      <ul className="divide-y divide-[#f0e6d8]">
        {jobs.map((job) => {
          const status = STATUS_LABELS[job.status] ?? {
            label: job.status,
            className: 'border-[#d4c4b0] text-[#5d4529]',
          };
          // サーバーの shareUrl ではなく公開アーカイブサイトのURLを出す（受付完了画面と同じ）
          const shareUrl = getArchiveVideoShareUrl(job.publicId);

          return (
            <li key={job.jobId} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:gap-3">
              <div className="flex items-center gap-2 sm:pt-0.5">
                <StatusIcon status={job.status} />
                <Badge variant="outline" className={`text-[10px] ${status.className}`}>
                  {status.label}
                </Badge>
              </div>

              <div className="min-w-0 flex-1">
                <p className="break-words text-sm text-[#2d1810]">
                  {job.title || '(タイトル未設定)'}
                </p>
                <p className="mt-0.5 text-xs text-[#8b6f47]">
                  <code>{job.publicId}</code>
                  <span className="ml-2">{formatDateTime(job.createdAt)}</span>
                </p>
                {job.error && <p className="mt-1 break-words text-xs text-[#b4462f]">{job.error}</p>}
              </div>

              {job.status === 'completed' && shareUrl && (
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0 text-xs text-[#8b6f47] underline hover:text-[#2d1810]"
                >
                  共有用URL
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
